import { InitialAdfoxAnalyticsPayload } from '@cian/postview-analytics-utils';
import { useDeviceType } from '@cian/ui-kit';
import { markAsClosed } from 'browser/map-search/utils/banner';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { AD_BANNER_MF_MANIFEST_SETTING_KEY } from '../../../common/constants/adBannerMicrofrontend';
import { getTermsValue } from '../../../common/packages/JsonQuery';
import { AdfoxBannerWrapper } from '../../components/AdfoxBannerWrapper';
import {
  ADFOX_SPECIAL_PROJECT_P_PARAMS,
  ADFOX_SPECIAL_PROJECT_SIZE,
  getAdfoxSpecialProjectPlace,
} from '../../constants/adfoxSpecialProject';
import { BANNER_ID, EBanners, EXPIRES_TIME_FOR_FSK, FSK_CLOSED_COOKIE_KEY } from '../../constants/banners';
import { FSK_BANNER } from '../../constants/fsk';
import { STONE_BANNER } from '../../constants/stone';
import { selectQueryString } from '../../selectors/queryString';
import { selectJsonQuery } from '../../selectors/results';
import { selectIsOpen } from '../../selectors/searchResults';
import { AdBannerMicrofrontendSpecialProjectContainer } from '../AdBannerMicrofrontendSpecialProjectContainer';
import { AdfoxBanner } from '../Adfox/components/AdfoxBanner';
import { AdfoxContextProvider } from '../Adfox/context/AdfoxContext';
import { useApplicationContext } from '../ApplicationContext';
import { useSelectedAdfoxBannerConfig } from '../BannersContainer/utils/useSelectedAdfoxBannerConfig';
import { trackClickBanner, trackCloseBanner, trackShowBanner } from '../FskBannerContainer/tracking';
import { ProPlusBannerContainer } from '../ProPlusBannerContainer';

import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

const DEVELOPER_LABEL_MAP: Record<number, string> = {
  [FSK_BANNER.developerId]: 'newbuilding_banner_fsk',
  [STONE_BANNER.developerId]: 'newbuilding_banner_stone',
};

interface IAdfoxSpecialProjectContainerProps {
  open: boolean;
  onClose(): void;
}

export function AdfoxSpecialProjectContainer({ open, onClose }: IAdfoxSpecialProjectContainerProps) {
  const deviceType = useDeviceType();
  const { config } = useApplicationContext();
  const queryString = useSelector(selectQueryString);
  const jsonQuery = useSelector(selectJsonQuery);
  const isSearchResultsOpen = useSelector(selectIsOpen);
  const selectedConfig = useSelectedAdfoxBannerConfig();
  const isDesktop = deviceType === 'desktop';

  /**
   * Разводка «микрофронтенд или легаси»: манифест лежит в конфиге, только если ручка
   * ad-banner-frontend ответила при включённом флаге MarTech.AdBannerMicrofrontend.Enabled.
   */
  const adBannerManifest = config.get<IMicrofrontendManifest>(AD_BANNER_MF_MANIFEST_SETTING_KEY);

  const [adfoxDegraded, setAdfoxDegraded] = React.useState(false);

  /**
   * У adfox-баннера два латча на весь маунт: adfoxDegraded (заглушка/ошибка прячет
   * баннер) и внутренний isHidden + `useMemo([isHidden])` в AdfoxBanner, который
   * фиксирует Ya.adfoxCode-инстанс с параметрами первого рендера. При смене региона
   * баннер снова может стать доступным (напр. возврат в МиМО для СТОУН, CD-279468),
   * но без вмешательства оба латча держат старое состояние — поэтому по `regionKey`:
   *  (1) сбрасываем adfoxDegraded (разблокирует рендер, если баннер деградировал);
   *  (2) перемонтируем `<AdfoxBanner key={regionKey}>` — свежий запрос adfox под
   *      новый регион, даже если в прошлом регионе баннер показывался (заморозка
   *      useMemo иначе оставила бы старые params — баг с открытой шторкой выдачи).
   * Пан/зум регион не меняют → regionKey стабилен → перезапросов/гонок Ya.adfoxCode нет.
   * Микрофронтенд перемонтируем по тому же ключу: он тоже запрашивает рекламу один раз
   * на маунт, а заодно получает новый placeId под новый контекст показа.
   */
  const regionKey = (getTermsValue('region')(jsonQuery) ?? []).join('-');
  React.useEffect(() => {
    setAdfoxDegraded(false);
  }, [regionKey]);

  const developerId = selectedConfig?.developerId ?? FSK_BANNER.developerId;
  const trackingLabel = DEVELOPER_LABEL_MAP[developerId];
  const isStone = developerId === STONE_BANNER.developerId;

  React.useEffect(() => {
    if (adfoxDegraded && isStone && !isDesktop) {
      const bannerBlock = document.getElementById(BANNER_ID);
      if (bannerBlock) {
        bannerBlock.style.height = '0';
      }
    }
  }, [adfoxDegraded, isStone, isDesktop]);

  const closeBanner = React.useCallback(() => {
    markAsClosed(FSK_CLOSED_COOKIE_KEY, EBanners.Fsk, EXPIRES_TIME_FOR_FSK);
    onClose();
    if (trackingLabel) {
      trackCloseBanner(queryString, deviceType, trackingLabel);
    }
  }, [deviceType, queryString, onClose, trackingLabel]);

  const handleClose = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      closeBanner();
    },
    [closeBanner],
  );
  const handleClick = React.useCallback(() => {
    if (trackingLabel) {
      trackClickBanner(queryString, deviceType, trackingLabel);
    }
  }, [deviceType, queryString, trackingLabel]);
  const handleDegrade = React.useCallback(() => setAdfoxDegraded(true), []);

  const postViewAnalyticsData = React.useMemo<InitialAdfoxAnalyticsPayload>(
    () => ({ place: getAdfoxSpecialProjectPlace(deviceType, developerId) }),
    [deviceType, developerId],
  );

  React.useEffect(() => {
    if (trackingLabel) {
      trackShowBanner(queryString, deviceType, trackingLabel);
    }

    // Не добавляю зависимости, чтобы аналитика отправилась только один раз при показе
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) {
    return null;
  }

  if (adfoxDegraded) {
    return isDesktop ? null : <ProPlusBannerContainer open={open} onClose={onClose} />;
  }

  if (adBannerManifest) {
    return (
      <AdfoxBannerWrapper>
        <AdBannerMicrofrontendSpecialProjectContainer
          key={regionKey}
          manifest={adBannerManifest}
          developerId={developerId}
          onStub={handleDegrade}
          onError={handleDegrade}
          onClose={closeBanner}
        />
      </AdfoxBannerWrapper>
    );
  }

  return (
    <AdfoxContextProvider
      pParams={ADFOX_SPECIAL_PROJECT_P_PARAMS[deviceType]}
      size={ADFOX_SPECIAL_PROJECT_SIZE[deviceType]}
      postViewAnalyticsData={postViewAnalyticsData}
      isSearchResultsOpen={isSearchResultsOpen}
      onTemplateClick={handleClick}
      onTemplateClose={handleClose}
    >
      <AdfoxBannerWrapper>
        <AdfoxBanner key={regionKey} onStub={handleDegrade} onError={handleDegrade} />
      </AdfoxBannerWrapper>
    </AdfoxContextProvider>
  );
}
